import { reactive } from "vue";

const OPEN_DELAY_MS = 300;
// Grace period so moving the pointer from the link across the small gap into the
// card doesn't dismiss it.
const CLOSE_DELAY_MS = 150;

export type TicketHoverTarget = {
    key: string;
    anchor: HTMLElement;
};

const state = reactive<{ target: TicketHoverTarget | null }>({ target: null });

let openTimer: ReturnType<typeof setTimeout> | undefined;
let closeTimer: ReturnType<typeof setTimeout> | undefined;

function cancelOpen() {
    if (openTimer) {
        clearTimeout(openTimer);
        openTimer = undefined;
    }
}

function cancelClose() {
    if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = undefined;
    }
}

function requestOpen(anchor: HTMLElement, key: string) {
    cancelClose();
    cancelOpen();
    openTimer = setTimeout(() => {
        state.target = { key, anchor };
    }, OPEN_DELAY_MS);
}

function requestClose() {
    cancelOpen();
    cancelClose();
    closeTimer = setTimeout(() => {
        state.target = null;
    }, CLOSE_DELAY_MS);
}

function closeNow() {
    cancelOpen();
    cancelClose();
    state.target = null;
}

/** State + controls for the single shared hover card (see TicketHoverCard.vue). */
export function useTicketHoverCardState() {
    return { state, keepOpen: cancelClose, requestClose, closeNow };
}

/**
 * Attaches hover/focus behaviour to any element - a Vue component root or a raw
 * DOM anchor rendered by markdown - so it opens the shared ticket card. Returns
 * a cleanup function.
 */
export function bindTicketHover(el: HTMLElement, key: string): () => void {
    function onEnter() {
        requestOpen(el, key);
    }
    function onLeave() {
        requestClose();
    }

    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    el.addEventListener("focus", onEnter);
    el.addEventListener("blur", onLeave);

    return () => {
        el.removeEventListener("mouseenter", onEnter);
        el.removeEventListener("mouseleave", onLeave);
        el.removeEventListener("focus", onEnter);
        el.removeEventListener("blur", onLeave);
    };
}
